export const mixf = (a, b, t) => a + (b - a) * t;
export const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
export const smooth = (t) => t * t * (3 - 2 * t);
export const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
export const MOBILE_FRAMING_HANDOFF_END = 1024;
export function mobileFramingMix(width, breakpoint) {
    if (width <= breakpoint)
        return 1;
    const handoffEnd = Math.max(MOBILE_FRAMING_HANDOFF_END, breakpoint + 1);
    const progress = clamp((width - breakpoint) / (handoffEnd - breakpoint), 0, 1);
    return 1 - smooth(progress);
}
export function hexToRgb(hex) {
    const value = hex.slice(1);
    const normalized = value.length === 3 ? value.split("").map((char) => char + char).join("") : value;
    const parsed = Number.parseInt(normalized, 16);
    return [((parsed >> 16) & 255) / 255, ((parsed >> 8) & 255) / 255, (parsed & 255) / 255];
}
export function resolveValues(values) {
    return {
        background: hexToRgb(values.background),
        zoom: values.zoom,
        ribbonWidthScale: values.ribbonWidthScale ?? 1,
        halftoneApparentZoom: values.halftoneApparentZoom ?? values.zoom,
        center: values.center,
        rotation: (values.rotationDeg * Math.PI) / 180,
        lineExtent: values.lineExtent,
        turbulence: values.turbulence,
        spacing: values.spacing,
        speed: values.speed,
        pointerStrengthScale: values.pointerStrengthScale ?? 1,
        pointerFunnelStrengthScale: values.pointerFunnelStrengthScale ?? values.pointerStrengthScale ?? 1,
        dither: values.dither,
        spatialColor: values.spatialColor,
        twistRetention: values.twistRetention,
        fanSpread: values.fanSpread,
        twistSpread: values.twistSpread,
        horizontalStretch: values.horizontalStretch,
        unravel: values.unravel,
        funnelStrength: values.funnelStrength ?? 0,
        funnelWidth: values.funnelWidth ?? 0.45,
        funnelTaperCeiling: values.funnelTaperCeiling ?? 1,
        funnelContourFloor: values.funnelContourFloor ?? 0,
        funnelTurbulence: values.funnelTurbulence ?? values.turbulence,
        funnelSpacing: values.funnelSpacing ?? values.spacing,
        funnelLineDefinition: values.funnelLineDefinition ?? 1,
        curveStrength: values.curveStrength,
        curveDisplacementScale: values.curveDisplacementScale ?? 1,
        curveProgress: values.curveProgress,
        effectOpacity: values.effectOpacity ?? 1,
        edgeReveal: values.edgeReveal ?? 1,
    };
}
export function resolveState(state, mobileMix, tablet) {
    const desktopValues = {
        ...state,
        ...(tablet ? state.tablet : undefined),
    };
    const resolved = resolveValues(desktopValues);
    if (!state.mobile || mobileMix <= 0)
        return resolved;
    const mobileValues = { ...desktopValues, ...state.mobile };
    return lerpState(resolved, resolveValues(mobileValues), mobileMix);
}
export const STRAIGHT_CURVE_PROFILE = {
    viewportSpan: 0,
    xCheckpoints: [0.5, 0.5, 0.5],
    bendWindows: [0, 1, 1, 2],
    startWidthScale: 1,
};
export function resolveCurveProfile(profile, mobileMix) {
    if (!profile)
        return STRAIGHT_CURVE_PROFILE;
    const desktop = {
        viewportSpan: profile.viewportSpan,
        xCheckpoints: profile.xCheckpoints,
        bendWindows: profile.bendWindows,
        startWidthScale: profile.startWidthScale ?? 1,
    };
    if (!profile.mobile || mobileMix <= 0)
        return desktop;
    const mobile = {
        viewportSpan: profile.mobile.viewportSpan ?? desktop.viewportSpan,
        xCheckpoints: profile.mobile.xCheckpoints ?? desktop.xCheckpoints,
        bendWindows: profile.mobile.bendWindows ?? desktop.bendWindows,
        startWidthScale: profile.mobile.startWidthScale ?? desktop.startWidthScale,
    };
    return {
        viewportSpan: mixf(desktop.viewportSpan, mobile.viewportSpan, mobileMix),
        xCheckpoints: [
            mixf(desktop.xCheckpoints[0], mobile.xCheckpoints[0], mobileMix),
            mixf(desktop.xCheckpoints[1], mobile.xCheckpoints[1], mobileMix),
            mixf(desktop.xCheckpoints[2], mobile.xCheckpoints[2], mobileMix),
        ],
        bendWindows: [
            mixf(desktop.bendWindows[0], mobile.bendWindows[0], mobileMix),
            mixf(desktop.bendWindows[1], mobile.bendWindows[1], mobileMix),
            mixf(desktop.bendWindows[2], mobile.bendWindows[2], mobileMix),
            mixf(desktop.bendWindows[3], mobile.bendWindows[3], mobileMix),
        ],
        startWidthScale: mixf(desktop.startWidthScale, mobile.startWidthScale, mobileMix),
    };
}
export function lerpState(a, b, t, shapeT = t) {
    return {
        background: [
            mixf(a.background[0], b.background[0], t),
            mixf(a.background[1], b.background[1], t),
            mixf(a.background[2], b.background[2], t),
        ],
        zoom: mixf(a.zoom, b.zoom, t),
        ribbonWidthScale: mixf(a.ribbonWidthScale, b.ribbonWidthScale, t),
        halftoneApparentZoom: mixf(a.halftoneApparentZoom, b.halftoneApparentZoom, t),
        center: [mixf(a.center[0], b.center[0], t), mixf(a.center[1], b.center[1], t)],
        rotation: mixf(a.rotation, b.rotation, t),
        lineExtent: mixf(a.lineExtent, b.lineExtent, t),
        turbulence: mixf(a.turbulence, b.turbulence, t),
        spacing: mixf(a.spacing, b.spacing, t),
        speed: mixf(a.speed, b.speed, t),
        pointerStrengthScale: mixf(a.pointerStrengthScale, b.pointerStrengthScale, t),
        pointerFunnelStrengthScale: mixf(a.pointerFunnelStrengthScale, b.pointerFunnelStrengthScale, t),
        dither: mixf(a.dither, b.dither, t),
        spatialColor: mixf(a.spatialColor, b.spatialColor, t),
        twistRetention: mixf(a.twistRetention, b.twistRetention, shapeT),
        fanSpread: mixf(a.fanSpread, b.fanSpread, shapeT),
        twistSpread: mixf(a.twistSpread, b.twistSpread, shapeT),
        horizontalStretch: mixf(a.horizontalStretch, b.horizontalStretch, shapeT),
        unravel: mixf(a.unravel, b.unravel, shapeT),
        funnelStrength: mixf(a.funnelStrength, b.funnelStrength, t),
        funnelWidth: mixf(a.funnelWidth, b.funnelWidth, t),
        funnelTaperCeiling: mixf(a.funnelTaperCeiling, b.funnelTaperCeiling, t),
        funnelContourFloor: mixf(a.funnelContourFloor, b.funnelContourFloor, t),
        funnelTurbulence: mixf(a.funnelTurbulence, b.funnelTurbulence, t),
        funnelSpacing: mixf(a.funnelSpacing, b.funnelSpacing, t),
        funnelLineDefinition: mixf(a.funnelLineDefinition, b.funnelLineDefinition, t),
        curveStrength: mixf(a.curveStrength, b.curveStrength, t),
        curveDisplacementScale: mixf(a.curveDisplacementScale, b.curveDisplacementScale, t),
        curveProgress: mixf(a.curveProgress, b.curveProgress, t),
        effectOpacity: mixf(a.effectOpacity, b.effectOpacity, t),
        edgeReveal: mixf(a.edgeReveal, b.edgeReveal, t),
    };
}
/** Resolve every authored state of a preset for the given framing. */
export function resolveStates(states, mobileMix, tablet) {
    return new Map(states.map((state) => [state.id, resolveState(state, mobileMix, tablet)]));
}
//# sourceMappingURL=state.js.map